import {
    addHyperlinkToFirstColumn,
    TableData,
    Cell,
    applyConditionalFormatting,
    pickColumns,
} from 'utils/data'
import { decodeHtmlEntities } from 'utils/helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { format } from 'date-fns'
import { formatTransactions } from 'utils/currency-helper'

type StoreTranslationKeys = typeof TRANSLATION_KEYS.storeFulfillment.inf.store

export interface InfInputRow {
    group_name: string
    inventory_not_found_percentage: number
    units: number
    dollars: number
    color_indicator: string
}

export interface InfDetailRow {
    order_no: string
    inventory_not_found_time: string
    inventory_not_found_units: number
    attempted_units: number
    dpci: string
    item_description: string
    team_member_name: string
    total_mins?: number
}

type AnyObj = Record<string, any>

export const INF_TABLES = [
    'total',
    'apparel/access',
    'essentials/beauty',
    'food/beverage',
    'hardlines',
    'home',
]

type CapabilityCell = { value: string }

export type CapabilityRow = { value: string }[]

type CapabilityHeadingKey = 'capability' | 'status' | 'reason' | 'last_updated'

type CapabilityHeading = { value: CapabilityHeadingKey }
export interface CapabilitiesData {
    headings: CapabilityHeading[]
    rows: CapabilityCell[][]
}

export const formattedTimeStamp = (timeStamp: string | number): string => {
    if (!timeStamp) return ''

    const date = new Date(timeStamp)

    const formattedDate = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(date)

    const timeZone = date
        .toLocaleTimeString('en-US', {
            hour12: true,
            timeZoneName: 'short',
        })
        .split(' ')[2]

    return `${formattedDate} ${timeZone}`
}

export const processRowData = (
    row: { [key: string]: any },
    colorMapping: { [key: string]: string | undefined },
    addPercentageSymbol: boolean = false,
    suppressIconKeys: string[] = [],
): Cell[] => {
    return Object.entries(row).map(([key, value]) => {
        const color = colorMapping[key]
        let cell = applyConditionalFormatting(value, color)
        if (addPercentageSymbol && !String(value).includes('%')) {
            const baseValue = String(value).trim()
            const symbol = cell.value.replace(baseValue, '').trim()
            cell.value = `${baseValue}%${symbol ? ` ${symbol}` : ''}`
        }
        if (key === 'tm_name') {
            cell.value = String(value)
        }
        if (suppressIconKeys.includes(key) && cell.icon) {
            delete cell.icon
        }
        return cell
    })
}

export const parseFormattedNumber = (value: string): number => {
    const sanitizedValue = value.replace(/,/g, '')

    if (sanitizedValue.endsWith('M')) {
        return parseFloat(sanitizedValue.replace('M', '')) * 1000000
    }
    return parseFloat(sanitizedValue) || 0
}

export const buildTableDataForFulfillment = (
    data: any,
    fulfillmentHeaders: string[],
    unitVolumeHeaders: string[],
    currentLocation: string,
) => {
    const rows: Array<{
        location_id: string
        inf_percent: number
        picked_on_time_percent: number
        drive_up_wait_time_threshold_percentage: number
    }> = []
    const unitVolumeRows: Array<{
        location_id: string
        late_units: number
        pickup_open_units: number
        picked_units_today: number
        total_pickup_forecasted_units: number
    }> = []

    const extractFulfillmentRowData = (location: any) => ({
        location_id: location.location_description,
        inf_percent: location.inf_percent,
        picked_on_time_percent: location.picked_on_time_percent,
        drive_up_wait_time_threshold_percentage:
            location.drive_up_wait_time_percentage_that_meets_time_goal,
    })

    const extractUnitVolumeRowData = (location: any) => ({
        location_id: location.location_description,
        late_units: location.late_units,
        pickup_open_units: location.pickup_open_units,
        picked_units_today: location.picked_units_today,
        total_pickup_forecasted_units: location.total_pickup_forecasted_units,
    })

    // Add rows only if the respective location types exist
    if (data?.parent_locations) {
        rows.push(...data.parent_locations.map(extractFulfillmentRowData))
        unitVolumeRows.push(
            ...data.parent_locations.map(extractUnitVolumeRowData),
        )
    }
    if (data?.current_location) {
        rows.push(extractFulfillmentRowData(data.current_location))
        unitVolumeRows.push(extractUnitVolumeRowData(data.current_location))
    }
    if (data?.children_locations) {
        const sortedChildrenLocations = [...data.children_locations].sort(
            (a, b) => {
                const aNumeric = parseInt(
                    a.location_description?.replace(/\D/g, '') || '0',
                )
                const bNumeric = parseInt(
                    b.location_description?.replace(/\D/g, '') || '0',
                )
                return aNumeric - bNumeric
            },
        )
        rows.push(...sortedChildrenLocations.map(extractFulfillmentRowData))
        unitVolumeRows.push(
            ...sortedChildrenLocations.map(extractUnitVolumeRowData),
        )
    }
    if (data?.grand_children_locations) {
        const sortedGrandChildrenLocations = [
            ...data.grand_children_locations,
        ].sort((a, b) => {
            const aNumeric = parseInt(
                a.location_description?.replace(/\D/g, '') || '0',
            )
            const bNumeric = parseInt(
                b.location_description?.replace(/\D/g, '') || '0',
            )
            return aNumeric - bNumeric
        })
        rows.push(
            ...sortedGrandChildrenLocations.map(extractFulfillmentRowData),
        )
        unitVolumeRows.push(
            ...sortedGrandChildrenLocations.map(extractUnitVolumeRowData),
        )
    }

    // Create locations array in the same order as rows (with sorting applied)
    const sortedChildrenLocations = data?.children_locations
        ? [...data.children_locations].sort((a, b) => {
              const aNumeric = parseInt(
                  a.location_description?.replace(/\D/g, '') || '0',
              )
              const bNumeric = parseInt(
                  b.location_description?.replace(/\D/g, '') || '0',
              )
              return aNumeric - bNumeric
          })
        : []

    const sortedGrandChildrenLocations = data?.grand_children_locations
        ? [...data.grand_children_locations].sort((a, b) => {
              const aNumeric = parseInt(
                  a.location_description?.replace(/\D/g, '') || '0',
              )
              const bNumeric = parseInt(
                  b.location_description?.replace(/\D/g, '') || '0',
              )
              return aNumeric - bNumeric
          })
        : []

    const locationsInRowOrder = [
        ...(data?.parent_locations || []),
        ...(data?.current_location ? [data.current_location] : []),
        ...sortedChildrenLocations,
        ...sortedGrandChildrenLocations,
    ]

    const fulfillmentTable: TableData = {
        headings: fulfillmentHeaders.map((header) => ({ value: header })),
        rows: rows.map((row, index) => {
            const location = locationsInRowOrder[index]

            return Object.entries(row).map(([key, value]) => {
                const color =
                    key === 'inf_percent'
                        ? location?.inf_percent_color
                        : key === 'picked_on_time_percent'
                          ? location?.picked_on_time_percent_color
                          : key === 'drive_up_wait_time_threshold_percentage'
                            ? location?.drive_up_wait_time_threshold_percentage_met
                                ? '#00a200'
                                : '#cc0000'
                            : undefined

                return applyConditionalFormatting(value, color)
            })
        }),
    }

    const unitVolumeTable: TableData = {
        headings: unitVolumeHeaders.map((header) => ({ value: header })),
        rows: unitVolumeRows.map((row, index) => {
            const location = locationsInRowOrder[index]

            return Object.entries(row).map(([key, value], columnIndex) => {
                const color =
                    key === 'late_units'
                        ? location?.late_units_color
                        : undefined

                const cell = applyConditionalFormatting(
                    columnIndex === 0
                        ? value
                        : formatTransactions(Number(value)),
                    color,
                )

                if (key === 'late_units' && cell.icon) {
                    delete cell.icon
                }
                return cell
            })
        }),
    }

    const fulfillmentTableWithLinks = addHyperlinkToFirstColumn(
        fulfillmentTable,
        currentLocation,
    )
    const unitVolumeTableWithLinks = addHyperlinkToFirstColumn(
        unitVolumeTable,
        currentLocation,
    )

    const combinedTables = [fulfillmentTableWithLinks, unitVolumeTableWithLinks]
    return { combinedTables }
}

export const buildSummaryTable = (
    data: InfInputRow[] = [],
    percentageLabel: string,
    store: StoreTranslationKeys,
    t: (key: string) => string,
): TableData => ({
    headings: [
        { value: t(store.department) },
        { value: t(store.units) },
        { value: t(store.dollars) },
        { value: t(percentageLabel) },
    ],
    rows: data.map((row) => [
        { value: row.group_name },
        { value: row.units?.toString() || '0' },
        {
            value:
                row.dollars != null
                    ? `$${Math.round(row.dollars).toString()}`
                    : '0',
        },
        applyConditionalFormatting(
            `${row.inventory_not_found_percentage || '0'}%`,
            row.color_indicator,
        ),
    ]),
})

export const buildDetailsTable = (
    data: InfDetailRow[] = [],
    store: StoreTranslationKeys,
    t: (key: string) => string,
): TableData => ({
    headings: [
        { id: 'orderNumber', value: t(store.orderNumber) },
        { id: 'infTime', value: t(store.infTime) },
        { id: 'infUnits', value: t(store.infUnits) },
        { id: 'attemptedUnits', value: t(store.attemptedUnits) },
        { id: 'dpci', value: t(store.dpci) },
        { id: 'item', value: t(store.item) },
        { id: 'teamMember', value: t(store.teamMember) },
        { id: 'totalMinutes', value: t(store.totalMinutes) },
    ],
    rows: data.map((row) => {
        const dpciCell: Cell = { value: row.dpci }

        if (row.dpci) {
            const currentUrl = new URL(window.location.href)
            const url = new URL('/item-search', window.location.origin)
            currentUrl.searchParams.forEach((value, key) => {
                url.searchParams.set(key, value)
            })
            url.searchParams.set('itemId', row.dpci)
            url.searchParams.set('fromLink', 'true')
            dpciCell.url = url.pathname + url.search
        }

        return [
            { value: row.order_no },
            { value: row.inventory_not_found_time },
            { value: row.inventory_not_found_units.toString() },
            { value: row.attempted_units.toString() },
            dpciCell,
            { value: decodeHtmlEntities(row.item_description) },
            { value: row.team_member_name },
            { value: row.total_mins?.toString() || '0' },
        ]
    }),
})
export const buildTableDataForStoresFormat = (
    data: any,
    fulfillmentHeaders: string[],
    unitVolumeHeaders: string[],
    teamMemberHeaders: string[],
) => {
    const fulfillmentRows: Array<{ [key: string]: any }> = []
    const unitVolumeRows: Array<{ [key: string]: any }> = []
    const teamMemberRows: Array<{ [key: string]: any }> = []

    const updatedFulfillmentHeaders = fulfillmentHeaders.slice(1)
    const updatedUnitVolumeHeaders = unitVolumeHeaders.slice(1)

    if (data?.order_pickup_unit) {
        fulfillmentRows.push({
            inf_percent:
                data.inventory_not_found.order_pickup.total_inventory_percent,
            picked_on_time_percent:
                data.order_pickup_unit.picked_on_time_percent,
            drive_up_wait_time_threshold_percentage:
                data.order_pickup_unit.drive_up
                    ?.drive_up_wait_time_percentage_that_meets_time_goal,
        })
    }

    if (data?.order_pickup_unit?.units_breakdown?.pickup) {
        unitVolumeRows.push({
            late_units:
                data.order_pickup_unit.units_breakdown.pickup.total_late_units,
            pickup_open_units:
                data.order_pickup_unit.units_breakdown.pickup.total_open_units,
            picked_units_today:
                data.order_pickup_unit.units_breakdown.pickup.units_picked,
            total_pickup_forecasted_units:
                data.order_pickup_unit.units_breakdown.pickup.forecasted_units,
        })
    }

    if (
        data?.order_pickup_unit?.team_member_productivity
            ?.team_member_productivity_details
    ) {
        teamMemberRows.push(
            ...data.order_pickup_unit.team_member_productivity.team_member_productivity_details.map(
                (detail: any) => ({
                    tm_name: detail.tm_name,
                    inf_percentage: detail.inf_percentage,
                    units_attempted: detail.units_attempted,
                    non_grocery_units_attempted:
                        detail.non_grocery_units_attempted,
                    grocery_units_attempted: detail.grocery_units_attempted,
                    total_pickup_productivity: detail.opu_pick_productivity,
                    inf_percentage_color: detail.inf_percentage_color,
                }),
            ),
        )
        teamMemberRows.sort(
            (a, b) => b.total_pickup_productivity - a.total_pickup_productivity,
        )
    }
    const fulfillmentTable: TableData = {
        headings: updatedFulfillmentHeaders.map((header) => ({
            value: header,
        })),
        rows: fulfillmentRows.map((row) =>
            processRowData(
                row,
                {
                    inf_percent:
                        data?.inventory_not_found.order_pickup?.color_indicator,
                    picked_on_time_percent:
                        Number(row.picked_on_time_percent) < 97
                            ? '#cc0000'
                            : '#00a200',
                    drive_up_wait_time_threshold_percentage: data
                        ?.order_pickup_unit?.drive_up
                        ?.drive_up_wait_time_threshold_percentage_met
                        ? '#00a200'
                        : '#cc0000',
                },
                true,
            ),
        ),
    }

    const unitVolumeTable: TableData = {
        headings: updatedUnitVolumeHeaders.map((header) => ({ value: header })),
        rows: unitVolumeRows.map((row) =>
            processRowData(
                row,
                {
                    late_units: row.late_units > 0 ? '#cc0000' : undefined,
                },
                false,
                ['late_units'],
            ),
        ),
    }

    const teamMemberTable: TableData = {
        headings: teamMemberHeaders.map((header) => ({ value: header })),
        rows: teamMemberRows.map((row) =>
            processRowData(row, {
                inf_percentage: row.inf_percentage_color,
            }),
        ),
    }

    const combinedTables = [fulfillmentTable, unitVolumeTable, teamMemberTable]
    return { combinedTables }
}
// The "column.key" order is not guaranteed to match the "columns.order" field.
// To avoid mismatches, we derive column indices dynamically instead of assuming a fixed order.
export function getInfColumnIndices(data: AnyObj) {
    const columns = data?.rows?.[0]?.values ?? []

    // column_key -> index map from the first row's values
    const keyToIndex: Record<string, number> = Object.create(null)
    for (let i = 0; i < columns.length; i++) {
        const k = columns[i]?.column_key
        if (k != null) keyToIndex[k] = i
    }

    // Return indices in the requested order (-1 if a key is missing)
    return INF_TABLES.map((k) => keyToIndex[k as string] ?? -1)
}

export const buildINFRGDTables = (
    pickupData: any,
    tableHeaders: string[],
    currentLocation: string,
    valuesIndices: number[],
    firstColumnHeader: string = '',
) => {
    if (!pickupData?.rows || !pickupData.columns) return []
    return valuesIndices
        .map((valuesIndex) => {
            const tableData: TableData = {
                headings: [
                    { value: firstColumnHeader },
                    ...tableHeaders.map((header) => ({ value: header })),
                ],
                rows: pickupData.rows
                    .map((row: any) => {
                        const valueData = row.values[valuesIndex]
                        if (!valueData) return null

                        return [
                            { value: row.location_description },
                            {
                                value: formatTransactions(
                                    Number(valueData.units || 0),
                                ),
                                raw: valueData.units?.toString() || '0',
                            },
                            {
                                value: `$${new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).format(Number(valueData.dollars || 0))}`,
                                raw: valueData.dollars?.toString() || '0',
                            },
                            {
                                ...applyConditionalFormatting(
                                    valueData.inf_percent || '0%',
                                    valueData.meets_threshold
                                        ? '#00a200'
                                        : '#cc0000',
                                ),
                                raw:
                                    valueData.inf_percent
                                        ?.replace('%', '')
                                        .trim() || '0',
                            },
                        ]
                    })
                    .filter(Boolean),
            }

            const tableWithLinks = addHyperlinkToFirstColumn(
                tableData,
                currentLocation,
            )

            return {
                table: tableWithLinks,
            }
        })
        .filter(Boolean)
}
export const buildStoresHistoricalData = (
    historicalData: any,
    fulfillmentHeaders: string[],
    unitVolumeHeaders: string[],
) => {
    if (!historicalData?.headings) {
        return { fulfillmentTable: null, unitVolumeTable: null }
    }

    const fulfillmentTable = pickColumns(historicalData, [7, 14, 11, 12])
    const unitVolumeTable = pickColumns(historicalData, [2, 8, 1])

    fulfillmentTable.headings = fulfillmentHeaders.map((header) => ({
        value: header,
    }))
    unitVolumeTable.headings = unitVolumeHeaders.map((header) => ({
        value: header,
    }))
    delete fulfillmentTable.footer
    delete unitVolumeTable.footer

    fulfillmentTable.rows = fulfillmentTable.rows.map((row) =>
        row.map((cell, index) => {
            const cellValue =
                typeof cell === 'object' && cell !== null ? cell.value : cell
            const cellColor =
                typeof cell === 'object' && cell !== null
                    ? cell.color
                    : undefined
            if ([0, 2, 3].includes(index)) {
                return applyConditionalFormatting(cellValue, cellColor)
            }
            return { value: cellValue }
        }),
    )

    const combinedTables = [fulfillmentTable, unitVolumeTable]

    return { combinedTables }
}

export function formatLocalWithDateFns(iso: string, lang = 'en-US') {
    const date = new Date(iso)
    // e.g. "7/22/25 @ 1:05PM"
    const base = format(date, 'M/d/yy @ h:mma')
    const baseLowercase = base.replace('AM', 'am').replace('PM', 'pm')
    const parts = new Intl.DateTimeFormat(lang, {
        timeZoneName: 'short',
    }).formatToParts(date)
    const timeZone = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    return `${baseLowercase} ${timeZone}`
}
